// Plain-text and shape helpers for a `richtext` value — a ProseMirror/TipTap document. The server (backend/engine/richtext.py) is the
// only thing that validates or renders one to HTML; this is the browser's side of the same projection, used for the cells, exports and
// previews that want words rather than a tree. Keep `docToText` in step with `to_text` there (the cases in richDoc.test.js mirror it).

export const EMPTY_DOC = { type: 'doc', content: [{ type: 'paragraph' }] };

export function isDoc(v) {
  return !!v && typeof v === 'object' && v.type === 'doc';
}

function walk(n, out) {
  if (!n || typeof n !== 'object') return;
  if (n.type === 'text') { out.push(String(n.text || '')); return; }
  if (n.type === 'hardBreak') { out.push('\n'); return; }
  if (n.type === 'horizontalRule') { out.push('\n\n'); return; }
  if (n.type === 'listItem') out.push('\n- ');
  (n.content || []).forEach((c) => walk(c, out));
  if (['paragraph', 'heading', 'blockquote', 'codeBlock', 'bulletList', 'orderedList'].includes(n.type)) out.push('\n\n');
}

export function docToText(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (!isDoc(v)) return '';
  const out = [];
  walk(v, out);
  return out.join('').replace(/\n{3,}/g, '\n\n').trim();
}

function hasRule(n) {
  return !!n && (n.type === 'horizontalRule' || (n.content || []).some(hasRule));
}

export function isEmptyDoc(v) {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return !v.trim();
  return !docToText(v).trim() && !hasRule(v);
}

// A plain string (an older longtext value, a pasted note) as a document: blank-line separated paragraphs, newlines as breaks.
export function textToDoc(s) {
  const paras = String(s ?? '').replace(/\r\n?/g, '\n').split(/\n\s*\n/).filter((p) => p.trim());
  const content = paras.map((p) => {
    const inline = [];
    p.replace(/^\n+|\n+$/g, '').split('\n').forEach((line, i) => {
      if (i) inline.push({ type: 'hardBreak' });
      if (line) inline.push({ type: 'text', text: line });
    });
    return inline.length ? { type: 'paragraph', content: inline } : { type: 'paragraph' };
  });
  return { type: 'doc', content: content.length ? content : [{ type: 'paragraph' }] };
}

const SAFE_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:'];

// Same rule as the server's `safe_href`: control characters are refused outright (a browser strips a tab inside `java\tscript:`).
export function safeHref(href) {
  if (typeof href !== 'string' || !href || href.length > 2000 || /[\x00-\x20\x7f-\x9f]/.test(href)) return null; // eslint-disable-line no-control-regex
  if (href.startsWith('#')) return href;
  try {
    const u = new URL(href);
    if (!SAFE_SCHEMES.includes(u.protocol)) return null;
    // The server reads the authority straight off the text (`http:///nohost` has none); the URL parser would invent one.
    if ((u.protocol === 'http:' || u.protocol === 'https:') && !/^https?:\/\/[^/?#]/i.test(href)) return null;
    return href;
  } catch { return null; }
}
